/**
 * `wayai bases sql …` — read-only SQL over a base's own data.
 *
 * Namespaced because WayAI already owns a tenant-SQL surface over conversation
 * analytics; these are different stores answering different questions.
 */

import { Command } from 'commander';
import { readFileSync } from 'node:fs';
import { createDataClient } from '../client.js';
import { printOutput } from '../output.js';
import { outputFormat, pathSegment, requireBase, withBaseOption } from '../helpers.js';
import { expected } from '../../lib/expected.js';

export function buildBasesSqlCommand(): Command {
  // See `buildBasesBatchCommand`: `--base` belongs on the subcommand here, not
  // on the `bases` namespace root.
  return withBaseOption(new Command('sql'))
    .description('Execute a read-only SQL query against base data')
    .argument('[query]', 'SQL query (SELECT only)')
    .option('--file <path>', 'Read SQL from a file instead of the argument')
    .option(
      '--param <kv...>',
      'Named parameters as key=value pairs (e.g. --param status=issued)',
    )
    .action(async function (
      this: Command,
      queryArg: string | undefined,
      opts: { file?: string; param?: string[] },
    ) {
      const base = pathSegment(requireBase(this), '--base');

      // `expected`, not a bare Error: every branch below is user input, and an
      // untagged throw reaches Sentry and the autofix triage queue.
      let query: string;
      if (opts.file) {
        try {
          query = readFileSync(opts.file, 'utf-8').trim();
        } catch (e) {
          throw expected(`--file ${opts.file}: ${e instanceof Error ? e.message : 'could not be read'}`);
        }
        if (!query) throw expected(`--file ${opts.file} is empty — it must contain a SELECT query.`);
      } else if (queryArg) {
        query = queryArg;
      } else {
        throw expected('Provide a SQL query as an argument or via --file.');
      }

      const params: Record<string, unknown> = {};
      for (const kv of opts.param ?? []) {
        // Split on the FIRST `=` only: a parameter value is frequently itself a
        // `key=value` string, and splitting on every `=` would truncate it.
        const eqIdx = kv.indexOf('=');
        if (eqIdx === -1) {
          throw expected(`Invalid --param ${JSON.stringify(kv)}, expected key=value.`);
        }
        params[kv.slice(0, eqIdx)] = kv.slice(eqIdx + 1);
      }

      const body: Record<string, unknown> = { query };
      if (Object.keys(params).length > 0) body.params = params;

      const client = await createDataClient();
      printOutput(await client.request('POST', `/v1/${base}/sql`, body), outputFormat(this));
    });
}
